import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { ApplicationConfig } from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter } from '@angular/router';
import { AuthInterceptor, NAIA_AUTH_BACKEND, NAIA_AUTH_ROUTES } from '@naia/auth-core';
import { NAIA_ENVIRONMENT } from '@naia/shared-interfaces';
import Aura from '@primeng/themes/aura';
import { ConfirmationService, MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';

import { PRIMENG_ES_EC_TRANSLATION } from './shared/constants/primeng-translation.constants';
import { environment } from '../environments/environment';
import { routes } from './app.routes';

/**
 * Configuración del **arranque STANDALONE** de este vertical (`main.ts`, puerto 4400).
 *
 * Bajo el shell de Naia —el modo que este ciclo entrega— **este archivo no corre**: el injector
 * raíz lo construye el host y el remoto entra por `entry.routes.ts`. Lo que sigue es el inventario
 * de lo que se pierde en ese modo y por qué no se repone desde acá:
 *
 * | Provider | Qué pasa bajo el shell |
 * |---|---|
 * | `provideHttpClient(withInterceptors([AuthInterceptor]))` | **No corre.** El `HttpClient` lo construye el shell y los interceptores se declaran al construirlo. Un remoto no tiene dónde registrar uno — de ahí que el contexto de tenant viaje como DATOS por `'./tenant'` y el interceptor sea del host. |
 * | `MessageService`, `ConfirmationService` | **Los provee el shell** en su injector raíz. No hay `NullInjectorError`. |
 * | `providePrimeNG({ theme })` | **No corre**; el tema lo pone el shell. |
 * | `NAIA_AUTH_BACKEND`, `NAIA_AUTH_ROUTES`, `NAIA_ENVIRONMENT` | **No corren**; los provee el shell desde su `config.json` de runtime. |
 * | `LOCALE_ID` / `registerLocaleData` | **Ya no están acá.** Native Federation no puede resolver `@angular/common/locales/*` —ni desde un remoto ni desde el host—, así que el locale lo registra el shell con una copia local de los datos, y su `LOCALE_ID` rige para los componentes de este vertical, que se construyen con el injector del host. |
 *
 * El `HttpErrorInterceptor` y el `TenantInterceptor` que vivían acá **no se perdieron: son del
 * shell**, que es el único lugar donde un interceptor puede registrarse bajo federación.
 */
export const appConfig: ApplicationConfig = {
    providers: [
        provideRouter(routes),
        provideHttpClient(withFetch(), withInterceptors([AuthInterceptor])),
        provideAnimationsAsync(),
        providePrimeNG({
            theme: { preset: Aura, options: { darkModeSelector: '.app-dark' } },
            translation: PRIMENG_ES_EC_TRANSLATION,
        }),
        // Único publicador de `environment` en el proyecto. El negocio inyecta este token, nunca
        // importa el archivo: bajo el shell estos providers no corren y el valor efectivo lo pone
        // el host desde su `config.json`.
        { provide: NAIA_ENVIRONMENT, useValue: environment },
        { provide: NAIA_AUTH_BACKEND, useValue: environment.servicesUri },
        // Rutas **del shell**, no de este proyecto: este vertical ya no tiene login propio
        // (`features/auth/` borrado) ni pantalla `/unauthorized`. Quien resuelve ambas es el host.
        { provide: NAIA_AUTH_ROUTES, useValue: { login: '/login', unauthorized: '/unauthorized' } },
        MessageService,
        ConfirmationService,
    ],
};
